/* eslint-disable no-use-before-define */
import { createSlice, createAsyncThunk, createSelector, createAction } from "@reduxjs/toolkit";
import { loadTreatment as loadTreatmentPromise, updateTreatment as updateTreatmentPromise } from "@amplio-common/treatments/api";
import { mapTSToStudentStatus, setStudentsStatus, clearAdmin } from "./administrationSlice";
import { toggleInSession } from "@app/routes/treatment/actions";
import { setStudioActivityPlaylist, studioSetStudentsStatus, studioMapTSToStudentStatus, cleareStudio } from "./studioSlice";
import { wsSetStudentsStatus, wsMapTSToStudentStatus, clearWS } from "./websocketSlice";
import { clearContentSlice, mapTSToStudentStatus as ContentMapTSToStudentStatus, contentSetStudentsStatus, selectPreviewBoardMode, setPreviewBoardMode, setLastVisitedLibrary, /*contentUpdateStudentStatus, */contentUpdateAllStudents } from "./contentSlice";
import { h5pGamesSetStudentsStatus, h5pGamesMapTSToStudentStatus, cleareH5P } from "./h5pGamesSlice";
import { articulationMapTSToStudentStatus, articulationSetStudentsStatus, clearArticulation } from "./articulationSlice";
import { speechMapTSToStudentStatus, speechSetStudentsStatus, clearSpeech } from "./speechSlice";
import { clearVideo, videoSetStudentsStatus, videoMapTSToStudentStatus } from "./videoSlice";
import { SERVICES/*, TREATMENT_STATUSES*/ } from "@app/constants";
import { VIDEO_SESSION_PAGES as VSP } from "@app/common/data/ActivitiessEnums";
import { simpleSetPayload } from "../constants";
import { clearStudent } from "./studentSlice";
import { youtubeSetStudentsStatus, youtubeMapTSToStudentStatus, clearYoutube } from "./youtubeSlice";
import { techStatusMapTSToStudentStatus, techStatusSetStudentsStatus } from "./techStatusSlice";
import { setStudentsReadingStatus, mapTSToReadingStudentStatus, ADAPTER_SYSTEM_USERS } from "@app/routes/activities/reading/store/readingSlice";
import { setStudentsSpellingStatus, mapTSToSpellingStudentStatus } from "@app/routes/activities/spelling/store/spellingSlice";
import { SPELLING_ADAPTER_SYSTEM_USERS } from "@app/routes/activities/spelling/store/initialState";
import { chatMapTSToStudentStatus, chatSetStudentsStatus } from "./chatSlice";
import { resetGamePlaylist, setGamePlayList } from "./activityLibrarySlice";
import { SLICE_NAMES } from "./sliceNames";
import { ROLES } from "@amplio-common/constants";
import { currentUser } from "@amplio-common/selectors/auth";

export const pushStudentsToNewTreatment = createAction(`${SLICE_NAMES.TREATMENT_SERVER_DATA_SLICE}/pushStudentsToNewTreatment`);

// Async actions
export const clearTreatment = createAsyncThunk(
  `${SLICE_NAMES.TREATMENT_SERVER_DATA_SLICE}/clearTreatment`,
  (_, thunkAPI) => {
    const { dispatch } = thunkAPI;
    dispatch(clearTreatmentData());
    dispatch(toggleInSession(false));

    // ---- These clears have to be first after clear treatment data! ----
    dispatch(clearAdmin());
    dispatch(clearContentSlice());
    // ---------------------------------

    dispatch(clearWS());
    dispatch(cleareStudio());
    dispatch(cleareH5P());
    dispatch(clearArticulation());
    dispatch(clearVideo());
    dispatch(clearSpeech());
    dispatch(clearYoutube());
    dispatch(clearStudent());
    dispatch(resetGamePlaylist());
  },
);

export const fetchTreatmentById = createAsyncThunk(
  `${SLICE_NAMES.TREATMENT_SERVER_DATA_SLICE}/fetchTreatmentById`,
  async (treatmentId, thunkAPI) => {
    const { response } = await loadTreatmentPromise(treatmentId);
    const { dispatch } = thunkAPI;
    let { treatment_students: ts } = response;
    const state = thunkAPI.getState();
    const currUser = currentUser(state);
    if (currUser.role === ROLES.STUDENT.id) { // This is workaround for the students when they enters with username and pw
      ts = [ts.find(s => s.student_id === currUser.id)];
    }

    dispatch(setStudentsStatus(ts.map(mapTSToStudentStatus)));
    dispatch(wsSetStudentsStatus(ts.map(wsMapTSToStudentStatus)));
    dispatch(studioSetStudentsStatus(ts.map(studioMapTSToStudentStatus)));
    dispatch(contentSetStudentsStatus(ts.map(ContentMapTSToStudentStatus)));
    dispatch(h5pGamesSetStudentsStatus(ts.map(h5pGamesMapTSToStudentStatus)));
    dispatch(articulationSetStudentsStatus(ts.map(articulationMapTSToStudentStatus)));
    dispatch(speechSetStudentsStatus(ts.map(speechMapTSToStudentStatus)));
    dispatch(videoSetStudentsStatus(ts.map(videoMapTSToStudentStatus)));
    dispatch(youtubeSetStudentsStatus(ts.map(youtubeMapTSToStudentStatus)));
    dispatch(techStatusSetStudentsStatus(ts.map(techStatusMapTSToStudentStatus)));
    dispatch(setStudentsReadingStatus([...ts.map(mapTSToReadingStudentStatus), ...ADAPTER_SYSTEM_USERS]));
    dispatch(setStudentsSpellingStatus([...ts.map(mapTSToSpellingStudentStatus), ...SPELLING_ADAPTER_SYSTEM_USERS]));
    dispatch(chatSetStudentsStatus(ts.map(chatMapTSToStudentStatus)));

    response.service === SERVICES.DYSLEXIA.id && dispatch(setStudioActivityPlaylist(response.activity_stack));
    response.service !== SERVICES.DYSLEXIA.id && dispatch(setGamePlayList(response.activity_stack));
    dispatch(toggleInSession(true));
    
    
    const previewBoardMode = selectPreviewBoardMode(state);
    if (previewBoardMode === null) {
      dispatch(setPreviewBoardMode(response.service === SERVICES.DYSLEXIA.id ? VSP.ACTIVITY : VSP.VOCABULARY));
    } else if (previewBoardMode === VSP.ACTIVITY && response.service !== SERVICES.DYSLEXIA.id) {
      dispatch(setPreviewBoardMode(VSP.VOCABULARY)); //switch to vocabulary if thetherapist isn't dyslexia
    } else {
      [VSP.ACTIVITY, VSP.VOCABULARY].includes(previewBoardMode) && dispatch(setLastVisitedLibrary(previewBoardMode)); //update last-visited-librbary if previewboard is library
    }

    // thunkAPI.dispatch(setPreviewBoardMode(response.service === SERVICES.SPEECH_LANGUAGE.id ? VSP.ACTIVITY_LEGACY : VSP.ACTIVITY));
    return response;
  },
);

export const updateTreatment = createAsyncThunk(
  `${SLICE_NAMES.TREATMENT_SERVER_DATA_SLICE}/update`,
  async ({ treatmentId, updateData }, thunkAPI) => {
    const { response } = await updateTreatmentPromise(treatmentId, updateData, false);
    const { dispatch } = thunkAPI;
    updateData?.treatment_students && dispatch(contentUpdateAllStudents(updateData.treatment_students));
    return response;
  },
);

const initialState = {
  isLoadingTreatment: true,
  serverData: null,
  isAfterLogin: true,
  err: null,
  facilitatorStudent: null,
  fetchTreatmentPendingTime: null,
  sv_actual_start: 0,
  drawingValue: {},
  isParticipentDrawing: false,
  userStatuses: [],
};

//define root selector

// const treatmentServerDataSlice_STATE_ROOT_SELECTOR = state => state.treatmentServerDataSlice;
// const rootSelector = s => createSelector(treatmentServerDataSlice_STATE_ROOT_SELECTOR, s);

export const treatmentServerDataSlice = createSlice({
  name: SLICE_NAMES.TREATMENT_SERVER_DATA_SLICE,
  initialState,
  reducers: {
    clearTreatmentData: () => ({ ...initialState }),
    setIsClearTreatment: simpleSetPayload("isClearTreatment"),
    setNewSession: simpleSetPayload("newSession"),
    setIsAfterLogin: simpleSetPayload("isAfterLogin"),
    setFacilitatorStudent: simpleSetPayload("facilitatorStudent"),
    setSv_actual_start: simpleSetPayload("sv_actual_start"),
    setDrawingValue: simpleSetPayload("drawingValue"),
    setIsParticipentDrawing: simpleSetPayload("isParticipentDrawing"),
    setUserStatuses: simpleSetPayload("userStatuses"),
    setIsParticipantUpdating: simpleSetPayload("isParticipantUpdating"),
  },
  extraReducers: {
    [fetchTreatmentById.pending]: (state) => { state.isLoadingTreatment = true; state.fetchTreatmentPendingTime = Date.now(); },
    [fetchTreatmentById.fulfilled]: (state, action) => {
      state.isLoadingTreatment = false;
      state.serverData = action.payload;
      state.sv_actual_start = action.payload.sv_actual_start;
      if (state.fetchTreatmentPendingTime) {
        state.fetchTreatmentPendingTime = Date.now() - state.fetchTreatmentPendingTime;
      }
    },
    [fetchTreatmentById.rejected]: (state, e) => {
      console.error("Problem setting up fetchTreatmentById", { e });
      state.isLoadingTreatment = false;
      state.err = Number(e.error.name) ? Number(e.error.name) : e;
      if (state.fetchTreatmentPendingTime) {
        state.fetchTreatmentPendingTime = Date.now() - state.fetchTreatmentPendingTime;
      }
    },
  },
});

export default treatmentServerDataSlice.reducer;

//actions

export const {
  clearTreatmentData,
  setIsAfterLogin,
  setFacilitatorStudent,
  setNewSession,
  setSv_actual_start,
  setDrawingValue,
  setIsParticipentDrawing,
  setUserStatuses,
  setIsParticipantUpdating,
} = treatmentServerDataSlice.actions;

//selectors
const simpleFieldSelector = (fieldName) => createSelector(
  [state => state[SLICE_NAMES.TREATMENT_SERVER_DATA_SLICE][fieldName]], field => field,
);
export const selectIsLoadingTreatment = simpleFieldSelector("isLoadingTreatment");
export const selectFetchError = simpleFieldSelector("err");
export const selectTreatment = simpleFieldSelector("serverData");
export const selectSv_actual_start = simpleFieldSelector("sv_actual_start");
export const selectDrawingValue = simpleFieldSelector("drawingValue");
export const selectIsParticipentDrawing = simpleFieldSelector("isParticipentDrawing");
export const selectUserStatuses = simpleFieldSelector("userStatuses");
export const selectIsParticipantUpdating = simpleFieldSelector("isParticipantUpdating");
export const selectIsClearTreatment = simpleFieldSelector("isClearTreatment");
export const selectIsAfterLogin = simpleFieldSelector("isAfterLogin");
export const selectFacilitatorStudent = simpleFieldSelector("facilitatorStudent");
export const selectFetchTreatmentPendingTime = simpleFieldSelector("fetchTreatmentPendingTime");
export const selectNewSession = simpleFieldSelector("newSession");
